import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const PaymentForm = ({ formData, updateFormData, onComplete }) => {
  const [paymentData, setPaymentData] = useState({
    name: '',
    businessName: '',
    cardNumber: '',
    expiryDate: '',
    securityCode: '',
    country: 'United States',
    paymentMethod: 'card'
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // Simulate payment processing
    setTimeout(() => {
      const organizationName = paymentData.businessName || `${paymentData.name}'s Studio`;
      onComplete({
        paymentMethod: paymentData.paymentMethod,
        organizationName,
        billingDetails: paymentData
      });
      setIsProcessing(false);
    }, 2000);
  };

  const handleFreeTrial = () => {
    const organizationName = paymentData.businessName || `${paymentData.name}'s Studio` || 'My Photography Studio';
    onComplete({
      paymentMethod: 'free_trial',
      organizationName,
      billingDetails: paymentData
    });
  };

  return (
    <div className="px-8 py-12 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h2 className="text-4xl font-bold text-white mb-6">
          Try the Professional plan for 30 days
        </h2>
        <p className="text-xl text-white/70 mb-6">
          Find answers across tools, build team workflows & automate with AI
        </p>
        
        {/* Plan Summary */}
        <div className="bg-brand/10 border border-brand/30 rounded-2xl p-6 mt-8 shadow-lg shadow-orange-500/10">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="font-semibold text-white text-lg">
                Professional Plan
              </h3>
              <p className="text-base text-white/70 mt-1">
                30-day free trial, then $24/member/month
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                $0
              </div>
              <div className="text-sm text-white/50">for 30 days</div>
            </div>
          </div>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-base font-medium text-white mb-3">
              Name
            </label>
            <input
              type="text"
              value={paymentData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white placeholder-white/50 transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-base font-medium text-white mb-3">
              Business name (optional)
            </label>
            <input
              type="text"
              value={paymentData.businessName}
              onChange={(e) => handleInputChange('businessName', e.target.value)}
              placeholder="Acme Photography"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white placeholder-white/50 transition-colors"
            />
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-base font-medium text-white mb-4">
            Payment method
          </label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleInputChange('paymentMethod', 'card')}
              className={`flex-1 p-4 rounded-xl border transition-all duration-300 ${
                paymentData.paymentMethod === 'card'
                  ? 'border-brand bg-brand/10 shadow-lg shadow-orange-500/20'
                  : 'border-white/10 hover:border-brand/50 bg-white/5'
              }`}
            >
              <div className="flex items-center justify-center gap-3">
                <CreditCardIcon className="w-6 h-6 text-white" />
                <span className="font-medium text-white">Card</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('paymentMethod', 'google_pay')}
              className={`flex-1 p-4 rounded-xl border transition-all duration-300 ${
                paymentData.paymentMethod === 'google_pay'
                  ? 'border-brand bg-brand/10 shadow-lg shadow-orange-500/20'
                  : 'border-white/10 hover:border-brand/50 bg-white/5'
              }`}
            >
              <div className="flex items-center justify-center gap-3">
                <GlobeAltIcon className="w-6 h-6 text-white" />
                <span className="font-medium text-white">Google Pay</span>
              </div>
            </button>
          </div>
        </div>

        {paymentData.paymentMethod === 'card' && (
          <>
            {/* Card Number */}
            <div>
              <label className="block text-base font-medium text-white mb-3">
                Card number
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={paymentData.cardNumber}
                  onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                  placeholder="1234 1234 1234 1234"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white placeholder-white/50 transition-colors"
                  required
                />
                <div className="absolute right-3 top-3 flex gap-2">
                  <div className="w-8 h-5 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">
                    VISA
                  </div>
                  <div className="w-8 h-5 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">
                    MC
                  </div>
                </div>
              </div>
            </div>

            {/* Expiry and Security Code */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-base font-medium text-white mb-3">
                  Expiration date
                </label>
                <input
                  type="text"
                  value={paymentData.expiryDate}
                  onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                  placeholder="MM / YY"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white placeholder-white/50 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-base font-medium text-white mb-3">
                  Security code
                </label>
                <input
                  type="text"
                  value={paymentData.securityCode}
                  onChange={(e) => handleInputChange('securityCode', e.target.value)}
                  placeholder="CVC"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white placeholder-white/50 transition-colors"
                  required
                />
              </div>
            </div>
          </>
        )}

        {/* Country */}
        <div>
          <label className="block text-base font-medium text-white mb-3">
            Country
          </label>
          <select
            value={paymentData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#FFB27D] focus:border-brand outline-none text-white transition-colors"
          >
            <option value="United States">United States</option>
            <option value="Canada">Canada</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Australia">Australia</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4 pt-6">
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full py-4 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-lg"
          >
            {isProcessing ? 'Starting your trial...' : 'Start Klorah Professional trial'}
          </button>
          
          <button
            type="button"
            onClick={handleFreeTrial}
            className="text-base text-brand hover:text-[#FFA164] transition-colors"
          >
            Continue with Free plan instead
          </button>
        </div>

        <p className="text-sm text-white/50 text-center mt-6">
          Your trial starts today. Cancel anytime during your trial and you won't be charged.
        </p>
      </form>
    </div>
  );
};

export default PaymentForm;